/**
 * What to do with the team a `?team_id=` link names.
 *
 * A link is written once and shared onward; by the time someone follows it the
 * team may have filled up. The link cannot know that, so the step has to decide
 * on arrival rather than trusting the URL.
 *
 * Kept pure and separate from the hook so the rule can be tested without
 * standing up the whole start step.
 */

import type {
  DivisionTeam,
  StartDivision,
} from '@sportninja/common/features/registration/startStep/types'

/** What the step should do with a resolved linked team. */
export type LinkedTeamOutcome =
  /** Select it, as the link asked. */
  | 'select'
  /**
   * Refuse it: the team is full. The caller tells the reader why, drops the
   * param so the stale id cannot be followed again, and keeps the team's
   * DIVISION as the list filter so the nearby alternatives are what they see.
   */
  | 'reject-full'

/**
 * Decide what a linked team resolves to.
 *
 * Only fullness is refused here, and the caller says what full means for this
 * reader (`isTeamFullFor`): a full roster, or — Jeff, Oct 1 — Team Budget slots
 * that are all taken or held for someone else, unless the reader's own invite
 * holds one. A team that cannot be found at all never reaches this —
 * `findDivisionTeamById` returns null and the pre-select simply does nothing,
 * which correctly leaves the reader choosing for themselves.
 */
export const linkedTeamOutcome = <T extends StartDivision>(
  entry: DivisionTeam<T> | null | undefined,
  isTeamFull: (entry: DivisionTeam<T>) => boolean
): LinkedTeamOutcome | null => {
  if (!entry) {
    return null
  }
  return isTeamFull(entry) ? 'reject-full' : 'select'
}
